import React, { useState, useEffect } from "react";
import Carousel from "../composant_reutilisable/Carousel";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import '../../CSS/Accueil.css';
import investissement from '../../images/ivestissement.png';
import tourisme from '../../images/tourisme.jpg';
import Menu from "../composant_reutilisable/Menu";
import FaqComponent from "../composant_reutilisable/Faq";
import LienUtile from "../composant_reutilisable/LienUtile";
import BeninInfo from "../composant_reutilisable/BeninInfo";
import ArticleComponent from "../composant_reutilisable/ArticleComponent";
import { useLanguageData } from '../../hooks/useLanguageData';
import { FaLinkedinIn } from "react-icons/fa";
import { FaTiktok } from "react-icons/fa";
import twitter from '../../images/twitter.png';

const Accueil = () => {
    const { t } = useTranslation();
    const [latestArticles, setLatestArticles] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [iframeHeight, setIframeHeight] = useState(600);

    const navigate = useNavigate();

    // 1. Hook pour les liens utiles (investissement et tourisme)
    const {
        data: linksData,
        loading: linksLoading,
        error: linksError
    } = useLanguageData('/api/v1/embassy/useful-links-category?slugCategory=lien-site');

    // 2. Hook pour les articles
    const {
        data: articlesData,
        loading: articlesLoading,
        error: articlesError
    } = useLanguageData('/api/v1/embassy/articles?page=1');

    // Observer pour détecter la taille de l'écran
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            setIframeHeight(mobile ? 500 : 600);
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Transformer les données des articles
    useEffect(() => {
        if (articlesData?.data && Array.isArray(articlesData.data)) {
            // console.log('📊 Articles reçus (avec langue):', articlesData);

            const twoLatestArticles = articlesData.data.slice(0, 2);
            const formattedArticles = twoLatestArticles.map((article) => ({
                id: article.id,
                slug: article.slug,
                link: article.url || `/article/${article.slug}`,
                date: `${article.articleCategory} ${new Date(article.date?.date || Date.now()).toLocaleDateString(
                    "fr-FR",
                    { day: "2-digit", month: "long", year: "numeric" }
                )}`,
                title: article.title,
                image: article.banner
                    ? article.banner.replace(/([^:]\/)\/+/g, "$1")
                    : "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR8bikI-KUuM1IWosgqDRS5jyv2U_PPYlG6Tg&s",
                alt: article.slug,
                category: article.articleCategory,
                fullArticle: article
            }));

            setLatestArticles(formattedArticles);
        }
    }, [articlesData]);

    // Trouver les données par slug
    const getDataBySlug = (slug) => {
        return linksData?.data?.find(item => item.slug === slug);
    };

    const investissementData = getDataBySlug("investir_benin_lien_un");
    const tourismeData = getDataBySlug("visiter_benin_lien_un");

    const handleArticleClick = (article) => {
        if (!article.slug) {
            console.error('❌ Article sans slug:', article);
            return;
        }
        navigate(`/article/${article.id}/${article.slug}`);  // Ajouter l'ID
    };

    // Écouter les messages de l'iframe pour ajuster la hauteur
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data && event.data.type === 'setHeight') {
                setIframeHeight(event.data.height);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    // Composant de spinner de chargement
    const LoadingSpinner = ({ size = "40px", color = "#1a3c5e" }) => (
        <div style={{
            textAlign: "center",
            padding: "3rem",
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <div style={{
                width: size,
                height: size,
                border: `3px solid #f3f3f3`,
                borderTop: `3px solid ${color}`,
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                marginBottom: "1rem"
            }}></div>

            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
        </div>
    );

    return (
        <div className="accueil-container">
            <Carousel />

            <div className="accueil-content">
                <Menu />

                {/* Section Investissement et Tourisme */}
                <div className="sections-grid">
                    {/* Section Investissement */}
                    <div className="section-investissement">
                        <div className="section-content">
                            <div className="text-content">
                                <div className="section-header">
                                    <h2 className="section-titre">{t('home.investmentTitle')}</h2>
                                </div>
                                <div className="section-body">
                                    {linksLoading ? (
                                        <LoadingSpinner size="30px" color="#1a3c5e" />
                                    ) : investissementData ? (
                                        <>
                                            <h3 className="section-sous-titre my-4" style={{ textTransform: "uppercase" }}>{investissementData.name}</h3>
                                            <div className="section-texte" dangerouslySetInnerHTML={{ __html: investissementData.description }} />
                                            <a href={investissementData.linkAccess} target="_blank" rel="noopener noreferrer" className="lien-decouverte">
                                                {investissementData.textBouton} ↗
                                            </a>
                                        </>
                                    ) : (
                                        <div className="info-indisponible" style={{
                                            padding: '2rem',
                                            textAlign: 'center',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '8px',
                                            color: '#6c757d',
                                            fontSize: '1.1rem'
                                        }}>
                                            Information indisponible
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="image-content">
                                {linksLoading ? (
                                    <LoadingSpinner size="30px" color="#1a3c5e" />
                                ) : investissementData && investissementData.imageBanner ? (
                                    <img
                                        src={investissementData.imageBanner}
                                        alt={investissementData.name || t('investment.alt')}
                                        className="section-image"
                                    />
                                ) : (
                                    <div className="image-placeholder" style={{
                                        width: '100%',
                                        height: '100%',
                                        minHeight: '300px',
                                        backgroundColor: '#e9ecef',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#6c757d',
                                        fontSize: '1rem'
                                    }}>
                                        Information indisponible
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Section Tourisme */}
                    <div className="section-tourisme">
                        <div className="section-content">
                            <div className="text-content">
                                <div className="section-header">
                                    <h2 className="section-titre2">{t('home.tourismTitle')}</h2>
                                </div>
                                <div className="section-body">
                                    {linksLoading ? (
                                        <LoadingSpinner size="30px" color="#1a3c5e" />
                                    ) : tourismeData ? (
                                        <>
                                            <h3 className="section-sous-titre2 my-4" style={{ textTransform: "uppercase" }}>{tourismeData.name}</h3>
                                            <div className="section-texte2" dangerouslySetInnerHTML={{ __html: tourismeData.description }} />
                                            <a href={tourismeData.linkAccess} target="_blank" rel="noopener noreferrer" className="lien-decouverte2">
                                                {tourismeData.textBouton} ↗
                                            </a>
                                        </>
                                    ) : (
                                        <div className="info-indisponible" style={{
                                            padding: '2rem',
                                            textAlign: 'center',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '8px',
                                            color: '#6c757d',
                                            fontSize: '1.1rem'
                                        }}>
                                            Information indisponible
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="image-content">
                                {linksLoading ? (
                                    <LoadingSpinner size="30px" color="#1a3c5e" />
                                ) : tourismeData && tourismeData.imageBanner ? (
                                    <img
                                        src={tourismeData.imageBanner}
                                        alt={tourismeData.name || t('tourism.alt')}
                                        className="section-image"
                                    />
                                ) : (
                                    <div className="image-placeholder" style={{
                                        width: '100%',
                                        height: '100%',
                                        minHeight: '300px',
                                        backgroundColor: '#e9ecef',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#6c757d',
                                        fontSize: '1rem'
                                    }}>
                                        Information indisponible
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section Dernières Actualités */}
                <div className="latest-articles-section">
                    <h2 className="section-title" style={{
                        textAlign: 'left',
                        marginTop: '40px',
                        marginBottom: '30px',
                        color: '#1a3c5e',
                        fontSize: '2rem',
                        fontWeight: '500'
                    }}>
                        {t('home.latestNewsTitle')}
                    </h2>

                    {articlesLoading ? (
                        <LoadingSpinner />
                    ) : latestArticles.length === 0 ? (
                        <p style={{ textAlign: "center" }}>
                            {t('latestNews.empty')}
                        </p>
                    ) : (
                        <>
                            <ArticleComponent
                                articles={latestArticles}
                                onArticleClick={handleArticleClick}
                            />
                        </>
                    )}
                </div>

                <LienUtile />
                <FaqComponent />
            </div>
        </div>
    );
};

export default Accueil;